import React, { useState, useMemo, useCallback } from "react";
import styles from "./style.module.scss";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../state";
import { isEqual } from "lodash";
import { QueryFullImportForPreview } from "../../../utils/graphql/hooks/importsHooks";
import {
  setImportPreview,
  setFullViewType,
} from "../../../state/importsReducer";

interface ImportSelectorProps {
  importName: string;
  importType: string;
}

export default function ImportSelector(props: ImportSelectorProps) {
  const dispatch = useDispatch();

  const queryFullImportForPreview = QueryFullImportForPreview();

  const [filterText, setFilterText] = useState("");

  const importPreviews = useSelector(
    (state: RootState) => state.imports?.importPreviews,
    isEqual,
  );

  const imports = useSelector(
    (state: RootState) => state.imports?.imports,
    isEqual,
  );

  const importSummaries = useSelector(
    (state: RootState) =>
      state.imports?.importSummaries[
        props.importType as keyof typeof state.imports.importSummaries
      ],
    isEqual,
  );

  const handleImportClick = (id: string) => {
    queryFullImportForPreview(id, props.importType);
  };

  const filteredImportSummaries = useMemo(() => {
    return importSummaries?.filter((importItem) =>
      importItem.name.toLowerCase().includes(filterText.toLowerCase()),
    );
  }, [importSummaries, filterText]);

  const selectedRowCallbackRef = useCallback(
    (node: HTMLTableRowElement | null) => {
      if (node) {
        requestAnimationFrame(() => {
          node.scrollIntoView({ behavior: "smooth", block: "center" });
        });
      }
    },
    [],
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        {props.importName}
        <div className={styles.filters}>
          <button
            className={styles.viewButton}
            onClick={() => dispatch(setFullViewType(props.importType))}
          >
            View
          </button>
          <button
            className={styles.resetButton}
            onClick={() => {
              dispatch(
                setImportPreview({
                  type: props.importType,
                  data: imports[props.importType as keyof typeof imports],
                }),
              );
            }}
          >
            Reset
          </button>
          <input
            type="text"
            placeholder="Filter imports..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            className={styles.filterInput}
          />
        </div>
      </div>
      <div className={styles.tableContainer}>
        <table className={styles.importsTable}>
          <tbody>
            {filteredImportSummaries?.length ? (
              filteredImportSummaries.map((importItem, index) => {
                const { _id, name, dt } = importItem;
                const isSelected =
                  importPreviews?.[
                    props.importType as keyof typeof importPreviews
                  ]?._id === _id;
                return (
                  <tr
                    key={index}
                    ref={isSelected ? selectedRowCallbackRef : null}
                    className={`${isSelected ? styles.selectedRow : ""}`}
                    onClick={() => {
                      handleImportClick(_id);
                    }}
                  >
                    <td>{name}</td>
                    <td className={styles.timestamp}>
                      {new Date(dt).toLocaleString()}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={2}>No imports available</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
